import React, { useEffect, useState } from 'react';
import { X, CheckCircle2, Copy, MessageCircle, PartyPopper } from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';

/**
 * NPS-01 — Net Promoter Score floating modal.
 *
 * Mounts once on the dashboard when `localStorage.pending_nps === 'true'`.
 * Three internal states:
 *   1. `survey`  — score selector + (optional) comment
 *   2. `thanks`  — short "obrigado pelo feedback" for scores < 9
 *   3. `referral` — sharing prompt for promoters (score ≥ 9)
 *
 * Position: fixed bottom-right floating card (~380px). Non-intrusive — the
 * user can still interact with the dashboard while the modal is open.
 *
 * The component calls `POST /nps/mark-shown` on mount so the modal is
 * recorded as seen even if the user closes the tab without responding.
 */
const REFERRAL_LINK = 'https://app.akalipilates.com';
const WHATSAPP_MESSAGE = (
  'Estou usando o Akali AI para documentar minhas consultas com IA. '
  + 'É incrível para fisioterapeutas! Confira: '
  + 'https://app.akalipilates.com'
);

export const NPSSurveyModal = ({ open, onClose }) => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [stage, setStage] = useState('survey'); // survey | thanks | referral
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  // On mount: tell the backend we showed the modal so we never re-show it,
  // regardless of whether the user responds or just bounces.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try { await api.npsMarkShown(); } catch { /* best-effort */ }
    })();
  }, [open]);

  // Auto-close thanks state after 3s
  useEffect(() => {
    if (stage !== 'thanks') return undefined;
    const t = setTimeout(() => onClose?.(), 3000);
    return () => clearTimeout(t);
  }, [stage, onClose]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (score === null || submitting) return;
    setSubmitting(true);
    try {
      const res = await api.npsRespond({ score, comment: comment.trim() || null });
      if (res?.show_referral) {
        setStage('referral');
      } else {
        setStage('thanks');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Não foi possível enviar agora.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismiss = async () => {
    // Survey state only — if we're already in thanks/referral, just close.
    if (stage === 'survey') {
      try { await api.npsDismiss(); } catch { /* best-effort */ }
    }
    onClose?.();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(REFERRAL_LINK);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      try { await api.npsReferralClicked({ channel: 'copy_link' }); } catch { /* best-effort */ }
    } catch {
      toast.error('Não foi possível copiar. Tente manualmente.');
    }
  };

  const handleWhatsApp = async () => {
    try { await api.npsReferralClicked({ channel: 'whatsapp' }); } catch { /* best-effort */ }
    window.open(
      `https://wa.me/?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const cardBg = isDark
    ? 'bg-slate-800 border-slate-700 text-slate-100'
    : 'bg-white border-slate-200 text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-600';

  return (
    <div
      className={`fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-32px)] rounded-2xl border shadow-xl p-5 ${cardBg}`}
      role="dialog"
      aria-labelledby="nps-title"
      data-testid="nps-survey-modal"
    >
      <button
        type="button"
        onClick={handleDismiss}
        className={`absolute top-3 right-3 p-1 rounded hover:opacity-70 ${muted}`}
        aria-label="Fechar"
        data-testid="nps-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>

      {stage === 'survey' && (
        <SurveyStage
          score={score}
          setScore={setScore}
          comment={comment}
          setComment={setComment}
          submitting={submitting}
          onSubmit={handleSubmit}
          isDark={isDark}
          muted={muted}
        />
      )}

      {stage === 'thanks' && (
        <div className="text-center py-2" data-testid="nps-thanks-stage">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
          <h3 className="text-base font-semibold mb-1">Obrigado pelo seu feedback!</h3>
          <p className={`text-sm ${muted}`}>Ele nos ajuda a melhorar o Akali AI.</p>
        </div>
      )}

      {stage === 'referral' && (
        <ReferralStage
          score={score}
          copied={copied}
          onCopy={handleCopy}
          onWhatsApp={handleWhatsApp}
          onClose={onClose}
          isDark={isDark}
          muted={muted}
        />
      )}
    </div>
  );
};


/* ============================== Sub-stages ============================== */

const SurveyStage = ({ score, setScore, comment, setComment, submitting, onSubmit, isDark, muted }) => {
  return (
    <>
      <h3 id="nps-title" className="text-base font-semibold pr-6" data-testid="nps-survey-title">
        Como está sendo sua experiência?
      </h3>
      <p className={`text-xs mt-0.5 ${muted}`}>Leva menos de 30 segundos.</p>

      <p className={`text-sm leading-relaxed mt-4 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
        De 0 a 10, qual a probabilidade de você recomendar o Akali AI para
        um colega fisioterapeuta?
      </p>

      <div className="mt-3 grid grid-cols-11 gap-1" data-testid="nps-score-row">
        {Array.from({ length: 11 }).map((_, i) => {
          const selected = score === i;
          return (
            <button
              key={i}
              type="button"
              onClick={() => setScore(i)}
              data-testid={`nps-score-${i}`}
              className={`h-8 rounded text-xs font-semibold transition ${
                selected
                  ? 'bg-primary text-white'
                  : (isDark
                      ? 'bg-slate-700 hover:bg-slate-600 text-slate-200'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700')
              }`}
              aria-pressed={selected}
            >
              {i}
            </button>
          );
        })}
      </div>
      <div className={`flex justify-between text-[10px] mt-1 ${muted}`}>
        <span>Nada provável</span>
        <span>Muito provável</span>
      </div>

      {score !== null && (
        <textarea
          rows={3}
          maxLength={500}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="O que motivou sua nota? (opcional)"
          data-testid="nps-comment"
          className={`mt-4 w-full text-sm rounded-md border px-3 py-2 ${
            isDark
              ? 'bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500'
              : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'
          }`}
        />
      )}

      <Button
        onClick={onSubmit}
        disabled={score === null || submitting}
        className="mt-4 w-full bg-primary hover:bg-primary-hover"
        data-testid="nps-submit-btn"
      >
        {submitting ? 'Enviando...' : 'Enviar →'}
      </Button>
    </>
  );
};

const ReferralStage = ({ score, copied, onCopy, onWhatsApp, onClose, isDark, muted }) => {
  return (
    <div data-testid="nps-referral-stage">
      <div className="text-center">
        <PartyPopper className="w-10 h-10 mx-auto mb-2 text-amber-500" />
        <h3 className="text-base font-semibold" data-testid="nps-referral-title">
          Que ótimo! Obrigado pela nota {score}!
        </h3>
        <p className={`text-xs mt-1 ${muted}`}>
          Conhece algum fisioterapeuta que também se beneficiaria do Akali AI?
        </p>
      </div>

      <div
        className={`mt-4 flex items-center gap-2 p-2 rounded border text-xs ${
          isDark ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-slate-200'
        }`}
      >
        <span className={`font-mono flex-1 truncate ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
          {REFERRAL_LINK}
        </span>
        <button
          type="button"
          onClick={onCopy}
          className={`shrink-0 text-xs font-medium px-2 py-1 rounded transition ${
            copied
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300'
              : 'bg-primary text-white hover:bg-primary-hover'
          }`}
          data-testid="nps-copy-btn"
        >
          {copied ? '✓ Copiado!' : (<><Copy className="inline w-3 h-3 mr-1" />Copiar link</>)}
        </button>
      </div>

      <Button
        onClick={onWhatsApp}
        className="mt-3 w-full text-white"
        style={{ backgroundColor: '#25D366' }}
        data-testid="nps-whatsapp-btn"
      >
        <MessageCircle className="w-4 h-4 mr-2" />
        Compartilhar via WhatsApp
      </Button>

      <button
        type="button"
        onClick={onClose}
        className={`mt-3 w-full text-xs underline underline-offset-2 ${muted} hover:opacity-80`}
        data-testid="nps-referral-dismiss"
      >
        Agora não
      </button>
    </div>
  );
};

export default NPSSurveyModal;
